import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Key, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [email, setEmail] = useState("");
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
      setTokenError("Lien invalide");
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
      if (res.data.valid) {
        setTokenValid(true);
        setEmail(res.data.email || "");
      } else {
        setTokenError(res.data.error || "Lien invalide");
      }
    } catch (err) {
      setTokenError("Impossible de vérifier le lien");
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success("Mot de passe modifié avec succès");
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur lors de la réinitialisation";
      toast.error(detail);
      if (detail.includes("expiré") || detail.includes("utilisé")) {
        setTokenValid(false);
        setTokenError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white border-slate-200 shadow-lg">
          <CardContent className="py-12 text-center">
            <Loader2 size={48} className="mx-auto text-[#26788A] animate-spin mb-4" />
            <p className="text-slate-600">Vérification du lien...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white border-slate-200 shadow-lg">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <CardTitle className="text-2xl font-semibold text-slate-900">
              Mot de passe modifié
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-600">
              Votre mot de passe a été réinitialisé avec succès.
              <br />
              Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </p>
            <Link to="/login">
              <Button className="w-full btn-primary py-3">
                Se connecter
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white border-slate-200 shadow-lg">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <XCircle size={32} className="text-red-600" />
            </div>
            <CardTitle className="text-2xl font-semibold text-slate-900">
              Lien invalide
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-600">
              {tokenError || "Ce lien de réinitialisation n'est plus valide."}
            </p>
            <p className="text-sm text-slate-500">
              Les liens expirent après 60 minutes ou après utilisation.
            </p>
            <div className="pt-4 space-y-3">
              <Link to="/forgot-password">
                <Button className="w-full btn-primary">
                  Demander un nouveau lien
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="w-full text-slate-600">
                  <ArrowLeft size={16} className="mr-2" />
                  Retour à la connexion
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white border-slate-200 shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-16 h-16 bg-[#26788A] rounded-full flex items-center justify-center mb-4">
            <Key size={32} className="text-white" />
          </div>
          <CardTitle className="text-2xl font-semibold text-slate-900">
            Nouveau mot de passe
          </CardTitle>
          {email && (
            <p className="text-slate-500 text-sm">
              Pour le compte : <strong>{email}</strong>
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-600 text-xs uppercase tracking-wider">
                Nouveau mot de passe
              </Label>
              <div className="relative mt-2">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-field pr-10"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoFocus
                  data-testid="reset-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Minimum 6 caractères
              </p>
            </div>
            <div>
              <Label className="text-slate-600 text-xs uppercase tracking-wider">
                Confirmer le mot de passe
              </Label>
              <div className="relative mt-2">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field pr-10"
                  placeholder="••••••••"
                  required
                  data-testid="reset-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-reset-confirm-password-visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3"
              data-testid="reset-submit-btn"
            >
              {loading ? "Modification..." : "Réinitialiser le mot de passe"}
            </Button>
            <Link to="/login">
              <Button variant="ghost" type="button" className="w-full text-slate-600">
                <ArrowLeft size={16} className="mr-2" />
                Retour à la connexion
              </Button>
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
